import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos da API ---------------------- */
export type StandbyItemAPI = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  produto: {
    id: number;
    codigo: string;
    nome: string;
    posicao: string | null;
  };

  pendente_retorno?: boolean | null;
  pendente_responsavel_id?: number | null;
  pendente_responsavel_nome?: string | null;
  pendente_criado_em?: string | null;
};

export type StandbyMovAPI = {
  movimentacao: {
    id: number;
    responsavel_id: number | null;
    responsavel_nome: string | null;
    departamento_origem: string | null;
    tipo: string | null;
    os_numero?: string | null;
    coletor_nome?: string | null;
    criado_em: string;

    embarcacao?: string | null;
    comentario?: string | null;
    empresa_cliente?: string | null;

    coordenadores_responsaveis_ids?: number[] | null;
    coordenadores_responsaveis_nomes?: string[] | null;

    pendente_retorno?: boolean | null;
    pendente_responsavel_id?: number | null;
    pendente_responsavel_nome?: string | null;
    pendente_criado_em?: string | null;
  };
  itens: StandbyItemAPI[];
};

type RespAPI = {
  ok: boolean;
  meta: any;
  data: StandbyMovAPI[];
};

/** ---------------------- Constantes ---------------------- */
const BASE = "https://api.eva-googlemarine.com/departamentos";
const URL = `${BASE}/coordenacao-v2/standby`;
const URL_AWAIT = `${BASE}/suprimentos/standby/await`;

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  const m = iso.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z$/
  );
  if (!m) {
    return { data: iso, hora: "" };
  }

  const [, ano, mes, dia, hora, minuto] = m;
  return { data: `${dia}/${mes}/${ano}`, hora: `${hora}:${minuto}` };
}

function formatBRL(x: number): string {
  return x.toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

/** Ordem da tabela */
export type StandbyRowUI = [
  string, // OS
  string, // Coordenador Responsável
  string, // Código
  string, // Nome do Produto
  string, // Quantidade
  string, // Custo Unitário
  string, // Técnico
  string, // Data
  string // Hora
];

/**
 * Agora a 2ª coluna usa movimentacao.coordenadores_responsaveis_nomes
 */
function flattenToRows(movs: StandbyMovAPI[]): StandbyRowUI[] {
  const rows: StandbyRowUI[] = [];

  for (const m of movs) {
    const { data, hora } = toBRT(m.movimentacao.criado_em);
    const os = m.movimentacao.os_numero || "-";

    let coord = "-";
    const nomes = Array.isArray(m.movimentacao.coordenadores_responsaveis_nomes)
      ? m.movimentacao.coordenadores_responsaveis_nomes.filter(Boolean)
      : [];

    if (nomes.length > 0) {
      coord = nomes.join(", ");
    }

    const tecnico = m.movimentacao.coletor_nome || "-";
    const pendMov = Boolean(m.movimentacao.pendente_retorno);

    for (const it of m.itens) {
      const qtd = Number(it.quantidade || 0);

      let custoNum = 0;
      if (it.custo_unitario != null) {
        const n = Number(String(it.custo_unitario).replace(",", "."));
        if (Number.isFinite(n)) custoNum = n;
      }

      const row: StandbyRowUI = [
        os,
        coord,
        it.produto.codigo,
        it.produto.nome,
        String(qtd),
        `R$ ${formatBRL(custoNum)}`,
        tecnico,
        data,
        hora,
      ];

      if (it.pendente_retorno === true || pendMov) {
        Object.defineProperty(row, "__pendente_retorno", {
          value: true,
          writable: false,
          enumerable: false,
          configurable: false,
        });
      }

      rows.push(row);
    }
  }

  return rows;
}

/** ---------------------- Fetchers ---------------------- */
export async function fetchStandbyOnce(): Promise<{
  rows: StandbyRowUI[];
  meta: RespAPI["meta"] | null;
  rawMovs: StandbyMovAPI[];
}> {
  const res = await fetch(URL, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const json = (await res.json()) as RespAPI;

  if (!json.ok) {
    return { rows: [], meta: json.meta ?? null, rawMovs: [] };
  }

  return {
    rows: flattenToRows(json.data),
    meta: json.meta ?? null,
    rawMovs: json.data,
  };
}

/** ---------------------- Await ---------------------- */
type AwaitResult = { changed: boolean; versao: number | null };

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${encodeURIComponent(
      since
    )}&timeout_ms=${AWAIT_TIMEOUT_MS}`;

    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });

    if (!res.ok) return null;

    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);

    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
export function useStandbySuprimentos() {
  const [rows, setRows] = useState<StandbyRowUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);

  const [rawByOS, setRawByOS] = useState<
    Record<
      string,
      { mov: StandbyMovAPI["movimentacao"]; itens: StandbyItemAPI[] }
    >
  >({});

  const stop = useRef(false);
  const fetching = useRef(false);

  const versaoRef = useRef<number | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  async function carregar() {
    fetching.current = true;
    try {
      const r = await fetchStandbyOnce();

      setRows(r.rows);
      setMeta(r.meta);

      const index: Record<
        string,
        { mov: StandbyMovAPI["movimentacao"]; itens: StandbyItemAPI[] }
      > = {};

      for (const m of r.rawMovs) {
        const os = m.movimentacao.os_numero || "-";
        if (!index[os]) index[os] = { mov: m.movimentacao, itens: [] };
        index[os].itens.push(...m.itens);
      }

      setRawByOS(index);
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    versaoRef.current = null;
    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();

    (async () => {
      try {
        await carregar();
        setLoading(false);

        const init = await awaitChange(-1);
        if (init && typeof init.versao === "number") {
          versaoRef.current = init.versao;
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        while (!stop.current) {
          while (fetching.current && !stop.current) await sleep(25);
          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();
            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;
            try {
              resp = await awaitChange(cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                await sleep(jitter(ERROR_BACKOFF_MS));
              }
            } finally {
              if (awaitCtrlRef.current === ac) awaitCtrlRef.current = null;
              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null) {
                awaitSupported.current = false;
              }
              await sleep(POLL_FALLBACK_MS);
              continue;
            } else if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);
            if (resp.changed || avancou) {
              versaoRef.current = resp.versao ?? cur;
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }
          } else {
            await sleep(POLL_FALLBACK_MS);
            await carregar();
          }
        }
      } catch {
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };
  }, []);

  return {
    rows,
    loading,
    meta,
    rawByOS,
    refresh: carregar,
  };
}
